import React from 'react';
import {
  LANDING_PROBLEM,
  LANDING_SOLUTION,
  LANDING_NETWORK,
  LANDING_DIAGRAM_ARIA,
} from '../../constants/landingCopy';
import styles from './LandingDiagrams.module.css';

function truncateLabel(s: string, max = 22): string {
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}

/** Word-wrap hub labels for small satellite nodes (full text stays in the list column). */
function wrapHubLabel(text: string, maxChars = 17, maxLines = 3): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';

  const pushCurrent = () => {
    if (current) {
      lines.push(current);
      current = '';
    }
  };

  for (const word of words) {
    if (lines.length >= maxLines) break;

    if (word.length > maxChars) {
      pushCurrent();
      if (lines.length < maxLines) {
        lines.push(`${word.slice(0, maxChars - 1)}…`);
      }
      continue;
    }

    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= maxChars) {
      current = candidate;
    } else {
      pushCurrent();
      current = word;
    }
  }
  pushCurrent();

  if (lines.length === 0) {
    return [text.length <= maxChars ? text : `${text.slice(0, maxChars - 1)}…`];
  }
  return lines.slice(0, maxLines);
}

/** Static SVG: disconnected tool silos (no shared hub). */
export const LandingProblemDiagram: React.FC<{ className?: string }> = ({ className }) => {
  const [a, b, c, d] = LANDING_PROBLEM.cards;
  return (
    <figure className={`${styles.diagramWrap} ${className ?? ''}`}>
      <svg
        className={styles.diagramSvg}
        viewBox="0 0 440 188"
        role="img"
        aria-label={LANDING_DIAGRAM_ARIA.problem}
      >
        <title>{LANDING_DIAGRAM_ARIA.problem}</title>
        <rect className={styles.nodeRect} x="16" y="20" width="100" height="48" rx="10" />
        <text x="66" y="48" className={styles.nodeText} textAnchor="middle">
          {truncateLabel(a.title, 26)}
        </text>
        <rect className={styles.nodeRect} x="168" y="12" width="100" height="48" rx="10" />
        <text x="218" y="40" className={styles.nodeText} textAnchor="middle">
          {truncateLabel(b.title, 26)}
        </text>
        <rect className={styles.nodeRect} x="320" y="24" width="100" height="48" rx="10" />
        <text x="370" y="52" className={styles.nodeText} textAnchor="middle">
          {truncateLabel(c.title, 26)}
        </text>
        <rect className={styles.nodeRect} x="120" y="112" width="100" height="48" rx="10" />
        <text x="170" y="140" className={styles.nodeText} textAnchor="middle">
          {truncateLabel(d.title, 26)}
        </text>
        <path
          className={styles.brokenLine}
          d="M 66 68 L 66 95 L 200 95 L 200 60"
        />
        <path
          className={styles.brokenLine}
          d="M 370 72 L 370 88 L 280 88"
        />
        <path
          className={styles.brokenLine}
          d="M 218 60 L 218 78 L 140 78 L 140 108"
        />
        <path
          className={styles.brokenLine}
          d="M 320 48 L 260 48 L 260 100"
        />
      </svg>
    </figure>
  );
};

/** Static SVG: Tranzit hub with spokes to each capability. */
export const LandingSolutionHubDiagram: React.FC<{ className?: string }> = ({ className }) => {
  const items = LANDING_SOLUTION.hubItems;
  const cx = 290;
  const cy = 210;
  const r = 148;
  const lineHeight = 12;
  const angles = [270, 330, 30, 90, 150, 210].map((deg) => (deg * Math.PI) / 180);
  const points = angles.map((t, i) => {
    const x = cx + r * Math.cos(t);
    const y = cy + r * Math.sin(t);
    return { x, y, label: items[i] ?? '', lines: wrapHubLabel(items[i] ?? '') };
  });

  return (
    <figure className={`${styles.diagramWrap} ${styles.diagramWrapWide} ${className ?? ''}`}>
      <svg
        className={styles.diagramSvg}
        viewBox="0 0 580 420"
        role="img"
        aria-label={LANDING_DIAGRAM_ARIA.solution}
      >
        <title>{LANDING_DIAGRAM_ARIA.solution}</title>
        {points.map((p, i) => (
          <line
            key={`spoke-${i}`}
            className={styles.spoke}
            x1={cx}
            y1={cy}
            x2={p.x}
            y2={p.y}
          />
        ))}
        <rect className={styles.hubCenter} x={cx - 56} y={cy - 22} width="112" height="44" rx="12" />
        <text x={cx} y={cy + 5} className={styles.hubLabel} textAnchor="middle">
          Tranzit
        </text>
        {points.map((p, i) => {
          const tw = 118;
          const th = Math.max(32, 12 + p.lines.length * lineHeight);
          const bx = p.x - tw / 2;
          const by = p.y - th / 2;
          const startY = p.y - ((p.lines.length - 1) * lineHeight) / 2 + 3;
          return (
            <g key={`sat-${i}`}>
              <rect className={styles.nodeRect} x={bx} y={by} width={tw} height={th} rx="8" />
              <text className={styles.satelliteLabel} textAnchor="middle">
                {p.lines.map((line, li) => (
                  <tspan key={li} x={p.x} y={startY + li * lineHeight}>
                    {line}
                  </tspan>
                ))}
              </text>
            </g>
          );
        })}
      </svg>
    </figure>
  );
};

/** Static SVG: four participant types around a central platform. */
export const LandingNetworkDiagram: React.FC<{ className?: string }> = ({ className }) => {
  const nodes = LANDING_NETWORK.nodes;
  const cx = 200;
  const cy = 150;
  const ro = 118;

  const layout = [
    { node: nodes[0], x: cx, y: 36, ax: cx, ay: 56 },
    { node: nodes[1], x: 354, y: cy, ax: 334, ay: cy },
    { node: nodes[2], x: cx, y: 264, ax: cx, ay: 244 },
    { node: nodes[3], x: 46, y: cy, ax: 66, ay: cy },
  ] as const;

  return (
    <figure className={`${styles.diagramWrap} ${className ?? ''}`}>
      <svg
        className={styles.diagramSvg}
        viewBox="0 0 400 300"
        role="img"
        aria-label={LANDING_DIAGRAM_ARIA.network}
      >
        <title>{LANDING_DIAGRAM_ARIA.network}</title>
        <circle className={styles.networkRing} cx={cx} cy={cy} r={ro} />
        {layout.map((L, i) => (
          <line
            key={`nspoke-${i}`}
            className={styles.networkSpoke}
            x1={cx}
            y1={cy}
            x2={L.ax}
            y2={L.ay}
          />
        ))}
        <rect className={styles.hubCenter} x={cx - 52} y={cy - 20} width="104" height="40" rx="10" />
        <text x={cx} y={cy + 6} className={styles.hubLabel} textAnchor="middle">
          Tranzit
        </text>
        {layout.map((L, i) => {
          const w = 92;
          const h = 34;
          return (
            <g key={`nn-${i}`}>
              <rect
                className={styles.networkOuter}
                x={L.x - w / 2}
                y={L.y - h / 2}
                width={w}
                height={h}
                rx="8"
              />
              <text x={L.x} y={L.y + 4} className={styles.networkOuterText} textAnchor="middle">
                {L.node}
              </text>
            </g>
          );
        })}
      </svg>
    </figure>
  );
};
